import React from 'react';
import { WebUiAlert } from 'src/modules/web-ui/components/alert';
import { WebUiButton } from 'src/modules/web-ui/components/button';

import { postWebUiApi } from '../../../api';
import WebUiPageWrapper from '../../../components/page-wrapper';
import { useWebUiGlobalContext } from '../../../context/global/use-global-context';
import CSystemDnsPanel from './c-system-dns-panel';
import CSystemFilesPanel from './c-system-files-panel';
import type {
  UsbApi,
  SystemId,
  DnsClient,
  SystemInfo,
  FilesGetResponse,
} from './c-system-settings-types';
import CSystemSystemInfoPanel from './c-system-system-info-panel';
import CSystemUsbPanel from './c-system-usb-panel';

export default function WebUiSettingsSystemPage() {
  const { currentUser } = useWebUiGlobalContext();
  const readonly = currentUser?.userLevel === 1;
  const [systemId, setSystemId] = React.useState<SystemId>({});
  const [info, setInfo] = React.useState<SystemInfo>({});
  const [dns, setDns] = React.useState<DnsClient>({});
  const [filesCfg, setFilesCfg] = React.useState<FilesGetResponse>({});
  const [usb, setUsb] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const [id, i, d, f, u] = await Promise.all([
      postWebUiApi<SystemId>({
        service: 'system',
        access: 'get',
        command: 'get id',
      }),
      postWebUiApi<SystemInfo>({
        service: 'system',
        access: 'get',
        command: 'get info',
      }),
      postWebUiApi<DnsClient>({
        service: 'system',
        access: 'get',
        command: 'dnsclient',
      }),
      postWebUiApi<FilesGetResponse>({
        service: 'system',
        access: 'get',
        command: 'files',
        parameters: {},
      }),
      postWebUiApi<UsbApi>({
        service: 'usbapi',
        access: 'get',
        command: 'state',
      }).catch(() => ({ state: false })),
    ]);
    setSystemId(id);
    setInfo(i);
    setDns(d);
    setFilesCfg(f);
    setUsb(Boolean(u.state));
  }, []);

  React.useEffect(() => {
    reload().catch((err) => setError((err as Error).message));
  }, [reload]);

  const runSet = React.useCallback(
    async (fn: () => Promise<void>, ok: string) => {
      try {
        await fn();
        setMessage(ok);
        setError(null);
        await reload();
      } catch (err) {
        setError((err as Error).message);
      }
    },
    [reload],
  );

  const flashOk = React.useCallback((msg: string) => {
    setMessage(msg);
    setError(null);
  }, []);

  const flashError = React.useCallback((msg: string) => {
    setError(msg);
  }, []);

  return (
    <WebUiPageWrapper titleList={['Settings', 'System']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="mb-2">
        <WebUiButton
          onClick={() => reload().catch((e) => setError((e as Error).message))}
        >
          Refresh
        </WebUiButton>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex flex-row gap-2">
          <div className="w-full">
            <CSystemSystemInfoPanel
              readonly={readonly}
              systemId={systemId}
              info={info}
              setInfo={setInfo}
              runSet={runSet}
            />
          </div>
          <div className="w-full">
            <CSystemDnsPanel
              readonly={readonly}
              dns={dns}
              setDns={setDns}
              runSet={runSet}
            />
          </div>
        </div>

        <CSystemUsbPanel
          readonly={readonly}
          usb={usb}
          setUsb={setUsb}
          runSet={runSet}
        />

        <CSystemFilesPanel
          readonly={readonly}
          data={filesCfg}
          webexport={Boolean(filesCfg.webexport)}
          onWebexportChange={(v) =>
            setFilesCfg((p) => ({ ...p, webexport: v }))
          }
          onSetFiles={() => {
            runSet(
              () =>
                postWebUiApi({
                  service: 'system',
                  access: 'set',
                  command: 'files',
                  parameters: { webexport: Boolean(filesCfg.webexport) },
                }),
              'Files settings updated.',
            ).catch(() => {});
          }}
          onReload={reload}
          onFlashOk={flashOk}
          onFlashError={flashError}
        />
      </div>
    </WebUiPageWrapper>
  );
}
